import styled from 'styled-components'

import { AuthLayout } from '../../components/AuthLayout'
import { Button } from '../../components/Button'
import { PageBody } from '../../components/PageBody'
import { PageLabel } from '../../components/PageLabel'
import { PanelUpsertUser } from '../../components/PanelUpsertUser'
import { PermissionsTable } from '../../components/PermissionsTable'
import { Spacer } from '../../components/Spacer'
import { TabContainer } from '../../components/TabContainer'
import { Tooltip } from '../../components/Tooltip'
import { MESSAGE_UNAUTHORIZED } from '../../content/tooltipMessages'
import { useAuthorized } from '../../hooks/useAuthorization'
import { useCompanyMembers } from '../../hooks/useCompanyMembers'
import { useMessage } from '../../hooks/useMessage'
import { usePanel } from '../../hooks/usePanel'
import type { DraftUser } from '../../models/client/user'
import { SPACING } from '../../styles/mixins/constants'
import { ValidationError } from '../../util/errors/ValidationError'

const Body = styled(PageBody)`
  display: grid;
  grid-template-areas:
    'header operations'
    'tabs tabs';
  grid-row-gap: ${SPACING[4]};
  grid-template-rows: auto 1fr;
  grid-template-columns: 1fr auto;
  overflow: auto;
`

const Operations = styled.div`
  grid-area: operations;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
`

const Header = styled(PageLabel)`
  grid-area: header;
`

const Tabs = styled(TabContainer)`
  grid-area: tabs;
`

export function Settings(): JSX.Element {
  const canManageUsers = useAuthorized('users')
  const sendMessage = useMessage()
  const { PanelComponent, setPanelVisibility } = usePanel<{
    isNew?: boolean
    draft?: DraftUser
  }>()
  const {
    editIsLoading,
    deleteIsLoading,
    companyMembers,
    addIsLoading,
    deleteUser,
    editUser,
    addUser,
  } = useCompanyMembers()

  const handleAdd = async (draft: DraftUser): Promise<void> => {
    try {
      await addUser(draft)
      setPanelVisibility(false)
      sendMessage.success(`Success: ${draft.name || 'user'} created!`)
    } catch (e) {
      if (e instanceof Error) {
        sendMessage.error(e)
      } else {
        sendMessage.danger('Something went wrong')
      }
    }
  }

  const handleEdit = async (draft: DraftUser, userId: string): Promise<void> => {
    try {
      await editUser(draft, userId)
      sendMessage.success(`Success: ${draft.name || 'user'} modified!`)
      setPanelVisibility(false)
    } catch (e) {
      if (e instanceof Error) {
        sendMessage.error(e)
      } else {
        sendMessage.danger('Something went wrong')
      }
    }
  }

  const handleDelete = async (userId: string): Promise<void> => {
    try {
      await deleteUser(userId)
    } catch (e) {
      if (e instanceof Error) {
        sendMessage.error(e)
      } else {
        sendMessage.danger('Something went wrong')
      }
    }
  }

  const handleSaveFor = (data) => {
    return async (draft) => {
      if (data?.isNew) {
        return handleAdd(draft)
      }

      const userId = data?.draft?.id

      if (!userId) {
        sendMessage.error(
          new ValidationError({
            propertyName: 'id',
            variableName: 'user',
            expectedPropertyValue: 'string',
            propertyValue: typeof userId,
            cause: new Error('System error, cannot edit this user'),
          })
        )
        return null
      }

      return handleEdit({ ...draft, id: userId }, userId)
    }
  }

  const isLoading = editIsLoading || deleteIsLoading || addIsLoading

  return (
    <AuthLayout>
      <PanelComponent
        onClose={() => setPanelVisibility(false)}
        renderChild={({ data }) => (
          <PanelUpsertUser
            operationsLoading={isLoading}
            isAdd={Boolean(data?.isNew)}
            canDelete={Boolean(canManageUsers)}
            canEditPermissions={Boolean(canManageUsers)}
            onClose={() => setPanelVisibility(false)}
            member={data?.draft || {}}
            onDelete={handleDelete}
            onSave={handleSaveFor(data)}
          />
        )}
      />
      <Body>
        <Header title="Settings" description="Manage user permissions." />
        <Operations>
          <Tooltip disabled={canManageUsers} tipText={MESSAGE_UNAUTHORIZED}>
            <Button
              disabled={isLoading || !canManageUsers}
              iconKey="UserAdd"
              type="primary"
              size="large"
              label="Add User"
              onClick={() => {
                setPanelVisibility({
                  isVisible: true,
                  data: {
                    isNew: true,
                  },
                })
              }}
              style={{
                display: !canManageUsers && 'none',
              }}
            />
          </Tooltip>
        </Operations>
        <Tabs
          activeKey="permissions"
          tabs={[
            {
              title: 'Users & Permissions',
              key: 'permissions',
              Component: (
                <>
                  <Spacer vertical size={2} />
                  <PermissionsTable
                    operationsLoading={isLoading}
                    members={companyMembers}
                    onEdit={(nextUserId) => {
                      const nextMember = companyMembers.find((member) => member.id === nextUserId)

                      if (!nextMember) {
                        return
                      }

                      setPanelVisibility({
                        data: {
                          draft: nextMember,
                        },
                      })
                    }}
                  />
                </>
              ),
            },
          ]}
        />
      </Body>
    </AuthLayout>
  )
}
